import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../app/store";
import { addLayer, setActiveLayerId } from "../../../reducers/editorSlice";
import TitleIcon from '@mui/icons-material/Title';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import Slider from '@mui/material/Slider';
import LayersList from "../layers-list";
import { clampNumber, getTextStyleLimits } from "../textStyleLimits";

interface Props {
  setActiveOverlay?: (overlay: string) => void;
}

const FONT_OPTIONS = [
  { name: 'Playfair Display', family: '"Playfair Display", serif' },
  { name: 'Inter', family: 'Inter, sans-serif' },
  { name: 'Roboto', family: 'Roboto, sans-serif' },
  { name: 'Arial', family: 'Arial, Helvetica, sans-serif' },
  { name: 'Times New Roman', family: '"Times New Roman", Times, serif' },
] as const;

function getTextContentError(value: unknown): string {
  const text = String(value ?? '');
  if (!text.length) return 'Text cannot be empty';
  if (!text.trim().length) return 'Text cannot contain only spaces';
  return '';
}

const TextSettingsSidebar: React.FC<Props> = ({ setActiveOverlay }) => {
  const dispatch = useDispatch();
  const activeLayerId = useSelector((state: RootState) => state.editor.activeLayerId);
  const selectedObjectProps = useSelector((state: RootState) => state.editor.selectedObjectProps);
  const currentDesign = useSelector((state: RootState) => state.editor.currentDesign);
  const canvasSize = typeof currentDesign?.canvas === 'object' && currentDesign.canvas
    ? currentDesign.canvas
    : { width: 2048, height: 2048 };
  const textLimits = getTextStyleLimits(canvasSize);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [textDraft, setTextDraft] = useState('');
  const [textError, setTextError] = useState('');
  const [fontMenuOpen, setFontMenuOpen] = useState(false);
  const [fontMenuUp, setFontMenuUp] = useState(false);
  const textFocusedRef = useRef(false);
  const fontMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onHistoryChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ canUndo?: boolean; canRedo?: boolean }>).detail || {};
      setCanUndo(!!detail.canUndo);
      setCanRedo(!!detail.canRedo);
    };
    window.addEventListener('editor-history-changed', onHistoryChanged);
    window.dispatchEvent(new CustomEvent('editor-history-request'));
    return () => window.removeEventListener('editor-history-changed', onHistoryChanged);
  }, []);

  useEffect(() => {
    textFocusedRef.current = false;
    setTextDraft(selectedObjectProps?.text ?? '');
    setTextError('');
    setFontMenuOpen(false);
  }, [activeLayerId]);

  useEffect(() => {
    if (!fontMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!fontMenuRef.current?.contains(e.target as Node)) setFontMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFontMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [fontMenuOpen]);

  useEffect(() => {
    if (textFocusedRef.current) return;
    setTextDraft(selectedObjectProps?.text ?? '');
    if (!getTextContentError(selectedObjectProps?.text ?? '')) {
      setTextError('');
    }
  }, [selectedObjectProps?.text]);

  useEffect(() => {
    const onInvalidText = (e: Event) => {
      const detail = (e as CustomEvent<{ layerId?: string; error?: string }>).detail || {};
      if (!detail.error || (detail.layerId && detail.layerId !== activeLayerId)) return;
      setTextError(detail.error);
    };
    window.addEventListener('editor-text-validation', onInvalidText);
    return () => window.removeEventListener('editor-text-validation', onInvalidText);
  }, [activeLayerId]);

  const handleUndo = () => {
    window.dispatchEvent(new CustomEvent('editor-undo'));
  };

  const handleRedo = () => {
    window.dispatchEvent(new CustomEvent('editor-redo'));
  };

  const handleAddText = () => {
    const layerId = `text-${Date.now()}`;
    const layerName = "New Text";

    dispatch(addLayer({ layerId, layerType: 'text', layerName }));
    dispatch(setActiveLayerId(layerId));

    const event = new CustomEvent('add-fabric-text', {
      detail: { text: "New Text", layerId, layerName }
    });
    window.dispatchEvent(event);
  };

  const handlePropChange = (prop: string, value: any) => {
    if (!activeLayerId) return;
    const event = new CustomEvent('update-fabric-props', {
      detail: { layerId: activeLayerId, props: { [prop]: value } }
    });
    window.dispatchEvent(event);
  };

  const commitTextValue = (value: string) => {
    const error = getTextContentError(value);
    if (error) {
      setTextError(error);
      return false;
    }
    const next = value.trim();
    setTextDraft(next);
    setTextError('');
    if (next !== (selectedObjectProps?.text ?? '')) {
      handlePropChange('text', next);
    }
    return true;
  };

  const handleTextChange = (value: string) => {
    setTextDraft(value);
    const error = getTextContentError(value);
    setTextError(error);
    if (error) return;
    handlePropChange('text', value);
  };

  const handleTextBlur = () => {
    textFocusedRef.current = false;
    commitTextValue(textDraft);
  };

  const selectedFont = FONT_OPTIONS.find((font) => font.name === selectedObjectProps?.fontFamily) || FONT_OPTIONS[0];

  const toggleFontMenu = () => {
    if (!activeLayerId) return;
    setFontMenuOpen((open) => {
      const next = !open;
      if (next && fontMenuRef.current) {
        const rect = fontMenuRef.current.getBoundingClientRect();
        setFontMenuUp(window.innerHeight - rect.bottom < 240);
      }
      return next;
    });
  };

  const handleFontSelect = (fontName: string) => {
    handlePropChange('fontFamily', fontName);
    setFontMenuOpen(false);
  };

  const fontSizeValue = clampNumber(selectedObjectProps?.fontSize || 48, textLimits.minFontSize, textLimits.maxFontSize);
  const charSpacingValue = clampNumber(selectedObjectProps?.charSpacing || 0, textLimits.minCharSpacing, textLimits.maxCharSpacing);
  const lineHeightValue = clampNumber(selectedObjectProps?.lineHeight || 1.16, textLimits.minLineHeight, textLimits.maxLineHeight);

  useEffect(() => {
    if (!activeLayerId || !selectedObjectProps) return;
    const oversize =
      (selectedObjectProps.fontSize || 0) > textLimits.maxFontSize
      || (selectedObjectProps.charSpacing || 0) > textLimits.maxCharSpacing
      || (selectedObjectProps.lineHeight || 0) > textLimits.maxLineHeight;
    if (!oversize) return;
    window.dispatchEvent(new CustomEvent('update-fabric-props', {
      detail: {
        layerId: activeLayerId,
        props: {
          fontSize: fontSizeValue,
          charSpacing: charSpacingValue,
          lineHeight: lineHeightValue,
        },
      },
    }));
  }, [activeLayerId, selectedObjectProps, textLimits.maxFontSize, textLimits.maxCharSpacing, textLimits.maxLineHeight, fontSizeValue, charSpacingValue, lineHeightValue]);
  const isBold = selectedObjectProps?.fontWeight === 'bold' || selectedObjectProps?.fontWeight === 700;
  const isItalic = selectedObjectProps?.fontStyle === 'italic';

  return (
    <>
      <div className="editor_card">
        <h3>Overlays</h3>
        <div className="overlay_tabs">
          <figure className="tab_btn active">
            <TitleIcon sx={{ fontSize: 20 }} />
            <figcaption>Text</figcaption>
          </figure>
          <figure className="tab_btn" onClick={() => setActiveOverlay?.('color')}>
            <ColorLensIcon sx={{ fontSize: 20 }} />
            <figcaption>Color</figcaption>
          </figure>
          <figure className="tab_btn" onClick={() => setActiveOverlay?.('upload')}>
            <img src="/static/images/upload.svg" alt="color_upload_icon" />
            <figcaption>Upload</figcaption>
          </figure>
        </div>
      </div>

      <div className={`editor_card text_customization ${fontMenuOpen ? 'font_menu_open' : ''}`}>
        <div className="text_settings_header">
          <h3>Text Settings</h3>
          <div className="text_settings_actions">
            <div className="text_history_btns">
              <button
                type="button"
                className="tool_btn"
                onClick={handleUndo}
                disabled={!canUndo}
              >
                <img src="/static/images/undo.svg" alt="" />
              </button>
              <button
                type="button"
                className="tool_btn"
                onClick={handleRedo}
                disabled={!canRedo}
              >
                <img src="/static/images/redo.svg" alt="" />
              </button>
            </div>
            <button type="button" className="add_text_btn" onClick={handleAddText}>
              + Add Text
            </button>
          </div>
        </div>

        <div className="text_input_box">
          <input
            type="text"
            value={textDraft}
            onChange={(e) => handleTextChange(e.target.value)}
            onFocus={() => { textFocusedRef.current = true; }}
            onBlur={handleTextBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
            placeholder="Enter text..."
            disabled={!activeLayerId}
            className={textError ? 'has_error' : ''}
            aria-invalid={!!textError}
          />
          {textError && <p className="text_input_error">{textError}</p>}
        </div>

        <div className="text_prop_row">
          <label htmlFor="font_dropdown_trigger">Font</label>
          <div className={`inline_dropdown ${fontMenuOpen ? 'is_open' : ''}`} ref={fontMenuRef}>
            <button
              id="font_dropdown_trigger"
              type="button"
              className={`font_dropdown_trigger ${fontMenuOpen ? 'open' : ''}`}
              onClick={toggleFontMenu}
              disabled={!activeLayerId}
              aria-haspopup="listbox"

              aria-expanded={fontMenuOpen}
            >
              <span style={{ fontFamily: selectedFont.family }}>{selectedFont.name}</span>
              <KeyboardArrowDownIcon />
            </button>
            {fontMenuOpen && (
              <div
                className={`font_dropdown_menu ${fontMenuUp ? 'open_up' : 'open_down'}`}
                role="listbox"
              >
                {FONT_OPTIONS.map((font) => (
                  <button
                    key={font.name}
                    type="button"
                    role="option"
                    aria-selected={selectedFont.name === font.name}
                    className={`font_dropdown_option ${selectedFont.name === font.name ? 'active' : ''}`}
                    style={{ fontFamily: font.family }}
                    onClick={() => handleFontSelect(font.name)}
                  >
                    {font.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="text_prop_row">
          <label>Style</label>
          <div className="alignment_group" style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 5 }}>
            <button
              className={`align_btn ${isBold ? 'active' : ''}`}
              onClick={() => handlePropChange('fontWeight', isBold ? 'normal' : 'bold')}
              disabled={!activeLayerId}
            >
              <FormatBoldIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              className={`align_btn ${isItalic ? 'active' : ''}`}
              onClick={() => handlePropChange('fontStyle', isItalic ? 'normal' : 'italic')}
              disabled={!activeLayerId}
            >
              <FormatItalicIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        </div>

        <div className="text_prop_row">
          <label>Size</label>
          <div className="slider_control">
            <Slider
              className="slider"
              value={fontSizeValue}
              min={textLimits.minFontSize}
              max={textLimits.maxFontSize}
              onChange={(_e, val) => handlePropChange('fontSize', val as number)}
              disabled={!activeLayerId}
            />
            <span className="value">{fontSizeValue}</span>
          </div>
        </div>

        <div className="text_prop_row">
          <label>Color</label>
          <input
            type="color"
            value={selectedObjectProps?.fill || "#000000"}
            onChange={(e) => handlePropChange('fill', e.target.value)}
            disabled={!activeLayerId}
            style={{ width: 30, height: 30, padding: 0, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }}
          />
        </div>

        <div className="text_prop_row">
          <label>Alignment</label>
          <div className="alignment_group">
            <button className={`align_btn ${selectedObjectProps?.textAlign === 'left' ? 'active' : ''}`} onClick={() => handlePropChange('textAlign', 'left')} disabled={!activeLayerId}><FormatAlignLeftIcon sx={{ fontSize: 16 }} /></button>
            <button className={`align_btn ${selectedObjectProps?.textAlign === 'center' || !selectedObjectProps?.textAlign ? 'active' : ''}`} onClick={() => handlePropChange('textAlign', 'center')} disabled={!activeLayerId}><FormatAlignCenterIcon sx={{ fontSize: 16 }} /></button>
            <button className={`align_btn ${selectedObjectProps?.textAlign === 'right' ? 'active' : ''}`} onClick={() => handlePropChange('textAlign', 'right')} disabled={!activeLayerId}><FormatAlignRightIcon sx={{ fontSize: 16 }} /></button>
          </div>
        </div>

        <div className="text_prop_row">
          <label>Letter Spacing</label>
          <div className="slider_control">
            <Slider
              className="slider"
              value={charSpacingValue}
              min={textLimits.minCharSpacing}
              max={textLimits.maxCharSpacing}
              onChange={(_e, val) => handlePropChange('charSpacing', val as number)}
              disabled={!activeLayerId}
            />
            <span className="value">{charSpacingValue}</span>
          </div>
        </div>

        <div className="text_prop_row">
          <label>Line Spacing</label>
          <div className="slider_control">
            <Slider
              className="slider"
              value={lineHeightValue}
              min={textLimits.minLineHeight}
              max={textLimits.maxLineHeight}
              step={0.1}
              onChange={(_e, val) => handlePropChange('lineHeight', val as number)}
              disabled={!activeLayerId}
            />
            <span className="value">{Math.round(lineHeightValue * 100)}%</span>
          </div>
        </div>
      </div>

      <div className="editor_card layers_card">
        <h3>Layers</h3>
        <LayersList />
      </div>
      <div style={{ height: '40px', flexShrink: 0 }} />
    </>
  );
};

export default TextSettingsSidebar;
